import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Search, Award, Calendar, Star, CheckCircle, ArrowRight, Users, Shield, TrendingUp, QrCode, BarChart3, Flame, Share2, Trophy } from 'lucide-react';
import { useState } from 'react';

const HomePage = () => {
    const navigate = useNavigate();
    const [searchCity, setSearchCity] = useState('');

    const handleSearch = (e) => {
        e.preventDefault();
        navigate(`/agents${searchCity ? `?city=${encodeURIComponent(searchCity)}` : ''}`);
    };

    const stats = [
        { value: '500+', label: 'Agents partenaires' },
        { value: '15K+', label: 'Vendeurs accompagnés' },
        { value: '98%', label: 'Taux de satisfaction' },
    ];

    const features = [
        {
            icon: <Users className="w-6 h-6" />,
            title: 'Agents vérifiés',
            description: 'Tous nos agents sont vérifiés et évalués par notre communauté.',
        },
        {
            icon: <Calendar className="w-6 h-6" />,
            title: 'Réservation en ligne',
            description: 'Prenez rendez-vous en quelques clics selon vos disponibilités.',
        },
        {
            icon: <Shield className="w-6 h-6" />,
            title: 'Transparence totale',
            description: 'Honoraires affichés clairement, sans mauvaise surprise.',
        },
        {
            icon: <TrendingUp className="w-6 h-6" />,
            title: 'Performance garantie',
            description: 'Nos Top Performers ont un taux de réussite exceptionnel.',
        },
    ];

    const topPerformersPerks = [
        'Performances mesurées et vérifiées',
        'Avis clients authentiques',
        'Engagements qualité stricts',
        'Transparence des honoraires',
    ];

    return (
        <div className="min-h-screen" data-testid="home-page">
            {/* Hero Section */}
            <section className="relative min-h-[90vh] flex items-center">
                {/* Background Image */}
                <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{
                        backgroundImage: `url('https://images.unsplash.com/photo-1706808849827-7366c098b317?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBsdXh1cnklMjBob3VzZSUyMGV4dGVyaW9yJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc3MjQ3MDAxNXww&ixlib=rb-4.1.0&q=85')`,
                    }}
                />
                <div className="hero-overlay" />

                {/* Content */}
                <div className="relative container-scalimmo pt-32 pb-20">
                    <div className="max-w-3xl">
                        <div className="inline-flex items-center gap-2 bg-scalimmo-gold/10 border border-scalimmo-gold/30 rounded-full px-4 py-2 mb-6">
                            <Award className="w-4 h-4 text-scalimmo-gold" />
                            <span className="text-sm font-medium text-scalimmo-navy">
                                Plus de 500 agents certifiés
                            </span>
                        </div>

                        <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-semibold text-scalimmo-navy leading-none mb-6">
                            Trouvez l'agent immobilier
                            <span className="block mt-2">qu'il vous faut</span>
                        </h1>

                        <p className="text-lg md:text-xl text-muted-foreground max-w-xl mb-10">
                            Scalimmo connecte les vendeurs avec les meilleurs agents immobiliers.
                            Prenez rendez-vous en ligne, comme chez le médecin.
                        </p>

                        {/* Search Form */}
                        <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3 max-w-lg">
                            <div className="relative flex-1">
                                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                                <Input
                                    type="text"
                                    placeholder="Votre ville..."
                                    value={searchCity}
                                    onChange={(e) => setSearchCity(e.target.value)}
                                    className="pl-12 h-14 rounded-full border-input text-base"
                                    data-testid="search-city-input"
                                />
                            </div>
                            <Button
                                type="submit"
                                className="btn-primary h-14 px-8"
                                data-testid="search-submit"
                            >
                                Rechercher
                                <ArrowRight className="w-4 h-4 ml-2" />
                            </Button>
                        </form>

                        {/* Quick Stats */}
                        <div className="flex items-center gap-8 mt-12">
                            {stats.map((stat, idx) => (
                                <div key={idx} className="text-center">
                                    <p className="text-2xl md:text-3xl font-heading font-semibold text-scalimmo-navy">
                                        {stat.value}
                                    </p>
                                    <p className="text-sm text-muted-foreground">{stat.label}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Features Section */}
            <section className="py-24 bg-scalimmo-sand">
                <div className="container-scalimmo">
                    <div className="text-center mb-16">
                        <h2 className="font-heading text-4xl md:text-5xl font-medium text-scalimmo-navy mb-4">
                            Pourquoi Scalimmo ?
                        </h2>
                        <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                            Une plateforme pensée pour simplifier votre recherche d'agent immobilier
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {features.map((feature, idx) => (
                            <div
                                key={idx}
                                className="bg-white rounded-xl p-6 shadow-soft hover:shadow-md transition-shadow"
                            >
                                <div className="w-12 h-12 bg-scalimmo-navy/5 rounded-lg flex items-center justify-center mb-4 text-scalimmo-navy">
                                    {feature.icon}
                                </div>
                                <h3 className="font-heading text-xl font-semibold text-scalimmo-navy mb-2">
                                    {feature.title}
                                </h3>
                                <p className="text-muted-foreground text-sm">
                                    {feature.description}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Top Performers Section */}
            <section className="py-24 md:py-32">
                <div className="container-scalimmo">
                    <div className="grid lg:grid-cols-2 gap-16 items-center">
                        {/* Content */}
                        <div>
                            <div className="elite-badge inline-flex mb-6">
                                <Award className="w-4 h-4" />
                                Programme exclusif
                            </div>

                            <h2 className="font-heading text-4xl md:text-5xl font-medium text-scalimmo-navy mb-6">
                                Découvrez nos
                                <span className="block text-scalimmo-gold">Top Performers</span>
                            </h2>

                            <p className="text-lg text-muted-foreground mb-8">
                                Seuls les agents d'exception accèdent au statut Top Performer.
                                Des critères stricts garantissent la qualité de leur accompagnement.
                            </p>

                            <ul className="space-y-4 mb-10">
                                {topPerformersPerks.map((perk, idx) => (
                                    <li key={idx} className="flex items-center gap-3">
                                        <CheckCircle className="w-5 h-5 text-scalimmo-gold flex-shrink-0" />
                                        <span className="text-scalimmo-navy">{perk}</span>
                                    </li>
                                ))}
                            </ul>

                            <Button
                                className="btn-gold"
                                onClick={() => navigate('/top-performers')}
                                data-testid="view-top-performers"
                            >
                                Voir les Top Performers
                                <ArrowRight className="w-4 h-4 ml-2" />
                            </Button>
                        </div>

                        {/* Image */}
                        <div className="relative">
                            <div className="aspect-[4/3] rounded-2xl overflow-hidden">
                                <img
                                    src="https://images.unsplash.com/photo-1696861270495-7f35c35c3273?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwyfHxoYW5kc2hha2UlMjBidXNpbmVzcyUyMGRlYWwlMjBjbG9zZSUyMHVwfGVufDB8fHx8MTc3MjQ3MDAxNnww&ixlib=rb-4.1.0&q=85"
                                    alt="Business handshake"
                                    className="w-full h-full object-cover"
                                />
                            </div>
                            {/* Floating Card */}
                            <div className="absolute -bottom-6 -left-6 bg-white rounded-xl p-4 shadow-lg border border-border/60 max-w-[200px]">
                                <div className="flex items-center gap-1 mb-2">
                                    {[1, 2, 3, 4, 5].map((i) => (
                                        <Star key={i} className="w-4 h-4 fill-scalimmo-gold text-scalimmo-gold" />
                                    ))}
                                </div>
                                <p className="text-sm text-scalimmo-navy font-medium">
                                    "Service exceptionnel, vente conclue en 3 semaines !"
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-24 bg-scalimmo-navy relative overflow-hidden">
                {/* Decorative blurs */}
                <div className="absolute top-0 right-0 w-96 h-96 bg-scalimmo-gold/10 rounded-full blur-3xl pointer-events-none" />
                <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl pointer-events-none" />

                <div className="container-scalimmo relative">
                    <div className="text-center mb-12">
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-scalimmo-gold/15 border border-scalimmo-gold/30 mb-5">
                            <Flame className="w-3.5 h-3.5 text-scalimmo-gold" />
                            <span className="text-xs font-medium text-scalimmo-gold uppercase tracking-wider">Nouveau pour les agents</span>
                        </div>
                        <h2 className="font-heading text-4xl md:text-5xl font-medium text-white mb-5">
                            Vous êtes agent immobilier ?
                        </h2>
                        <p className="text-lg text-white/70 max-w-2xl mx-auto">
                            Scalimmo n'est pas juste un annuaire. C'est votre <strong className="text-white">cockpit professionnel</strong> :
                            planning, carte de visite digitale, statistiques temps réel et bannières prêtes à imprimer.
                        </p>
                    </div>

                    {/* Feature grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-12 max-w-5xl mx-auto">
                        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-5 hover:bg-white/10 transition-colors" data-testid="agent-feature-planning">
                            <div className="w-10 h-10 rounded-lg bg-scalimmo-gold/15 flex items-center justify-center mb-3">
                                <Calendar className="w-5 h-5 text-scalimmo-gold" />
                            </div>
                            <h3 className="font-heading text-base font-semibold text-white mb-1">
                                Planning en ligne
                            </h3>
                            <p className="text-sm text-white/60 leading-relaxed">
                                Vos vendeurs réservent un créneau en 1 minute, 24/7. Plus de coups de fil ratés.
                            </p>
                        </div>

                        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-5 hover:bg-white/10 transition-colors" data-testid="agent-feature-card">
                            <div className="w-10 h-10 rounded-lg bg-scalimmo-gold/15 flex items-center justify-center mb-3">
                                <QrCode className="w-5 h-5 text-scalimmo-gold" />
                            </div>
                            <h3 className="font-heading text-base font-semibold text-white mb-1">
                                Carte de visite digitale
                            </h3>
                            <p className="text-sm text-white/60 leading-relaxed">
                                Lien public + QR code + vCard. Bannières imprimables pour vos panneaux "À vendre".
                            </p>
                        </div>

                        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-5 hover:bg-white/10 transition-colors" data-testid="agent-feature-stats">
                            <div className="w-10 h-10 rounded-lg bg-scalimmo-gold/15 flex items-center justify-center mb-3">
                                <BarChart3 className="w-5 h-5 text-scalimmo-gold" />
                            </div>
                            <h3 className="font-heading text-base font-semibold text-white mb-1">
                                Statistiques temps réel
                            </h3>
                            <p className="text-sm text-white/60 leading-relaxed">
                                Vues, conversion, sources de trafic. Voyez quelle bannière convertit le mieux.
                            </p>
                        </div>

                        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-5 hover:bg-white/10 transition-colors" data-testid="agent-feature-ranking">
                            <div className="w-10 h-10 rounded-lg bg-scalimmo-gold/15 flex items-center justify-center mb-3">
                                <Trophy className="w-5 h-5 text-scalimmo-gold" />
                            </div>
                            <h3 className="font-heading text-base font-semibold text-white mb-1">
                                Classement & boost
                            </h3>
                            <p className="text-sm text-white/60 leading-relaxed">
                                Top performers mis en avant. Plus vous êtes actif, plus vous remontez dans la recherche.
                            </p>
                        </div>
                    </div>

                    {/* Trial highlight */}
                    <div className="bg-scalimmo-gold/10 border border-scalimmo-gold/30 rounded-xl p-4 max-w-3xl mx-auto mb-10 flex items-center gap-3">
                        <Share2 className="w-5 h-5 text-scalimmo-gold shrink-0" />
                        <p className="text-sm text-white/90">
                            <strong className="text-scalimmo-gold">Plan Starter gratuit</strong> avec accès complet aux statistiques pendant 6 mois d'essai.
                            Sans engagement, sans carte bancaire.
                        </p>
                    </div>

                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                        <Button
                            className="btn-gold"
                            onClick={() => navigate('/register?type=agent')}
                            data-testid="cta-register-agent"
                        >
                            Devenir agent partenaire
                            <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                        <Button
                            variant="outline"
                            className="rounded-full border-white/30 text-white hover:bg-white/10 px-8 py-3"
                            onClick={() => navigate('/pricing')}
                        >
                            Voir les tarifs
                        </Button>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default HomePage;
